import { motion } from 'framer-motion'
import { ChevronRightIcon, Loader2 } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect } from 'react'
import useFetchRecentsInfinite from '~/src/hooks/useFetchRecentsInfinite'
import type { UnifiedRecentItem } from '~/src/hooks/useFetchRecents'
import { cn } from '~/src/lib/utils'
import { useInView } from 'react-intersection-observer'

const MotionLink = motion(Link)

const MenuRecents: React.FC = () => {
  const {
    data: recents = [],
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useFetchRecentsInfinite({
    limit: 10,
    includeTotal: true,
  })

  // Reference to the loading indicator at the bottom
  const { ref: loadMoreRef, inView } = useInView()

  // Auto-load next page when the loading indicator comes into view
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  if (isLoading) return null
  if (!recents?.length) return null

  return (
    <div className='flex flex-col gap-1 px-4'>
      <div className='flex items-center justify-between py-1.5'>
        <div className='text-lg font-medium opacity-70'>Recent Searches</div>
        {/* unhide this and make this header linkable once we have a proper profile page */}
        <ChevronRightIcon className='hidden h-5 w-5 opacity-50' />
      </div>
      <div className='flex flex-col gap-2'>
        {recents?.map((recent: UnifiedRecentItem, index: number) => (
          <MotionLink
            key={recent?.id || recent?.hash || index}
            href={recent?.url}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index < 10 ? index * 0.05 : 0, duration: 0.3 }}
            className={cn('group flex flex-shrink-0 items-center gap-2')}
          >
            <div className='relative aspect-square size-10 shrink-0 overflow-hidden rounded-lg object-cover md:size-12'>
              {!!recent?.image && (
                <Image
                  src={recent.image}
                  alt=''
                  unoptimized
                  width={50}
                  height={50}
                  className='h-full w-full object-cover'
                />
              )}
              <div className='absolute inset-0 bg-black/5' />
            </div>
            <div className='line-clamp-2 text-sm/tight opacity-70'>
              {recent?.title || recent?.summary}
            </div>
          </MotionLink>
        ))}

        {/* Loading indicator for infinite scroll */}
        <div ref={loadMoreRef} className='flex justify-center py-2'>
          {isFetchingNextPage && (
            <Loader2 className='h-5 w-5 animate-spin opacity-50' />
          )}
        </div>
      </div>
    </div>
  )
}

export default MenuRecents
